/**
 * マッピング設定 Excel ダウンロード: 行モデル構築。
 *
 * 実装計画 6.2 / 6.3 に対応する。 IF02095（データカテゴリ・データ集類）× IF02090（項目一覧）× IF02085（マッピング） × IF02092（判定ルール）を突き合わせ、Excel の 1 行 = 1
 * レコードのフラット配列を返す。
 *
 * 重要:
 *
 * - **出力対象は IF02095 が返すデータ集類のみ**。IF02090 は期間を問わず全件返すため、 絞り込まないと月次タスクの Excel に年次・四半期の集類が混入する（実装計画 9-6）。
 * - セル結合は行わないため、A〜D 列の値は各行に繰り返して持たせる。
 */
import {
  formatDataTypeLabel,
  formatJudgmentRuleContent,
  formatMappingContent,
  formatMappingMethodLabel,
  formatNumAggTypeLabel
} from './mappingExportFormatter'

/** ステータス表示文字列 */
export const EXPORT_STATUS = {
  CONFIGURED: '設定済',
  UNSET: '未設定',
  REMOVED: '不要',
  JUDGMENT_RULE: '判定ルール適用'
}

/** 実在するデータ集類ではない予約コード（標準フォーマット専用項目） */
const SYSTEM_ONLY_DATA_KIND_CODES = new Set(['none', 'GDK-9999'])

/**
 * @typedef {Object} MappingExportRow
 * @property {string} categoryName - A 列: データカテゴリ
 * @property {string} dataKindName - B 列: データ集類
 * @property {string} columnName - C 列: 項目名
 * @property {string} dataType - D 列: データ型
 * @property {string} status - E 列: ステータス
 * @property {string} mappingMethod - F 列: マッピング方法
 * @property {number | string} priority - G 列: 優先度
 * @property {string} numAggType - H 列: 集計方法
 * @property {string} fileType - I 列: ファイル種別
 * @property {string} sourceColumnName - J 列: 元カラム名
 * @property {string} content - K 列: マッピング内容
 */

/**
 * オブジェクトから最初に見つかったキーの値を返す
 *
 * @param {Record<string, unknown> | null | undefined} obj
 * @param {...string} keys
 * @returns {unknown}
 */
function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined
  for (const key of keys) {
    const value = /** @type {Record<string, unknown>} */ (obj)[key]
    if (value !== undefined && value !== null) return value
  }
  return undefined
}

/**
 * 値をレコードとして扱う
 *
 * @param {unknown} value
 * @returns {Record<string, unknown>}
 */
function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? /** @type {Record<string, unknown>} */ (value)
    : {}
}

/**
 * 値を配列として扱う
 *
 * @param {unknown} value
 * @returns {unknown[]}
 */
function asArray(value) {
  return Array.isArray(value) ? value : []
}

/**
 * IF02090 のレスポンスを「データ集類コード → 項目一覧」の Map に変換する。 システム専用コードと、当該四半期の対象外の項目は除外する。
 *
 * @param {unknown[]} allChrofyColumns
 * @param {string | null | undefined} currentQuarter - `1Q`〜`4Q`。月次・年次では null
 * @returns {Map<string, { columnCode: string; columnName: string; dataType: string }[]>}
 */
function buildColumnsByDataKind(allChrofyColumns, currentQuarter) {
  /** @type {Map<string, { columnCode: string; columnName: string; dataType: string }[]>} */
  const map = new Map()

  for (const entry of allChrofyColumns ?? []) {
    const record = asRecord(entry)
    const dataKindCode = String(pick(record, 'data_classification_code', 'dataClassificationCode') ?? '').trim()
    if (dataKindCode === '' || SYSTEM_ONLY_DATA_KIND_CODES.has(dataKindCode)) continue

    const columns = pick(record, 'columns', 'Columns')
    const list = Array.isArray(columns) ? columns : []
    map.set(
      dataKindCode,
      list
        .filter((column) => isTargetQuarterColumn(asRecord(column), currentQuarter))
        .map((column) => {
          const col = asRecord(column)
          return {
            columnCode: String(pick(col, 'column_code', 'columnCode') ?? ''),
            columnName: String(pick(col, 'column_name', 'columnName') ?? ''),
            dataType: String(pick(col, 'column_data_type', 'columnDataType', 'data_type') ?? '')
          }
        })
    )
  }

  return map
}

/**
 * 項目が当該四半期の対象かどうか。
 *
 * マッピング設定画面（IF02083）の `is_target_quarter` と同じ判定にそろえ、 Excel と画面の表示内容を一致させる。
 *
 * - 対象四半期が未設定（空）の項目は全期間で対象
 * - 四半期が判定できない（月次・年次）ときは絞り込まない
 * - それ以外は対象四半期に現在四半期が含まれる場合のみ対象
 *
 * @param {Record<string, unknown>} column
 * @param {string | null | undefined} currentQuarter - `1Q`〜`4Q`。月次・年次では null
 * @returns {boolean}
 */
function isTargetQuarterColumn(column, currentQuarter) {
  if (!currentQuarter) return true
  const targetQuarter = pick(column, 'target_quarter', 'targetQuarter', 'TargetQuarter')
  const list = asArray(targetQuarter)
  if (list.length === 0) return true
  return list.map((quarter) => String(quarter).trim()).includes(String(currentQuarter).trim())
}

/**
 * IF02085 のマッピングを「データ集類コード::項目コード」でグルーピングし、優先度昇順に整列する。
 *
 * @param {unknown[]} mappings
 * @returns {Map<string, Record<string, unknown>[]>}
 */
function buildMappingsByField(mappings) {
  /** @type {Map<string, Record<string, unknown>[]>} */
  const map = new Map()

  for (const entry of mappings ?? []) {
    const record = asRecord(entry)
    const dataKindCode = String(
      pick(record, 'generic_task_code', 'genericTaskCode', 'data_kind_code', 'dataKindCode') ?? ''
    ).trim()
    const columnCode = String(pick(record, 'chrofy_column_code', 'chrofyColumnCode') ?? '').trim()
    if (dataKindCode === '' || columnCode === '') continue

    const key = `${dataKindCode}::${columnCode}`
    const list = map.get(key) ?? []
    list.push(record)
    map.set(key, list)
  }

  for (const list of map.values()) {
    list.sort((a, b) => Number(pick(a, 'priority', 'Priority') ?? 0) - Number(pick(b, 'priority', 'Priority') ?? 0))
  }

  return map
}

/**
 * IF02092 の判定ルールを引けるようにする。
 *
 * IF02092 は `model_dump(by_alias=True)` のため **PascalCase**（`DataKindCode` 等）で返る。
 *
 * データ集類コードが空のルールもあり得るため、 「データ集類::項目」と「項目のみ」の 2 通りのキーで登録し、 前者を優先して引く。
 *
 * @param {unknown[]} judgmentRules
 * @returns {Map<string, Record<string, unknown>>}
 */
function buildJudgmentRulesByField(judgmentRules) {
  /** @type {Map<string, Record<string, unknown>>} */
  const map = new Map()

  for (const entry of judgmentRules ?? []) {
    const record = asRecord(entry)
    const dataKindCode = String(pick(record, 'DataKindCode', 'data_kind_code', 'dataKindCode') ?? '').trim()
    const columnCode = String(pick(record, 'ChrofyColumnCode', 'chrofy_column_code', 'chrofyColumnCode') ?? '').trim()
    if (columnCode === '') continue

    if (dataKindCode !== '') {
      map.set(`${dataKindCode}::${columnCode}`, record)
    }
    // 項目コードのみのフォールバック（既存分を上書きしない）
    if (!map.has(columnCode)) {
      map.set(columnCode, record)
    }
  }

  return map
}

/**
 * マッピング情報からファイル種別・元カラム名を取り出す（I 列 / J 列）。 マッピングタイプごとに格納先が異なるため、既知のソース情報を順に探す。
 *
 * @param {unknown} mappingInfo
 * @returns {{ fileType: string; sourceColumnName: string }}
 */
function readSourceColumn(mappingInfo) {
  const info = asRecord(mappingInfo)
  const candidates = [
    'simple_mapping',
    'simpleMapping',
    'SimpleMapping',
    'conversion_table',
    'conversionTable',
    'ConversionTable',
    'value_mapping',
    'valueMapping',
    'ValueMapping',
    'source_date_format',
    'sourceDateFormat',
    'SourceDateFormat',
    'source_time_format',
    'sourceTimeFormat',
    'SourceTimeFormat'
  ]

  for (const key of candidates) {
    const source = asRecord(info[key])
    const fileType = pick(source, 'mapping_file_type', 'mappingFileType', 'MappingFileType')
    const columnName = pick(source, 'mapping_column_name', 'mappingColumnName', 'MappingColumnName')
    if (fileType !== undefined || columnName !== undefined) {
      return {
        fileType: String(fileType ?? ''),
        sourceColumnName: String(columnName ?? '')
      }
    }
  }

  return { fileType: '', sourceColumnName: '' }
}

/**
 * 1 項目分のマッピングからステータスを判定する（実装計画 6.3）
 *
 * @param {Record<string, unknown>[]} mappingRows
 * @returns {string}
 */
function resolveStatus(mappingRows) {
  if (mappingRows.length === 0) return EXPORT_STATUS.UNSET

  const types = mappingRows.map((row) => String(pick(row, 'mapping_type', 'mappingType') ?? ''))
  if (types.includes('judgment_rule')) return EXPORT_STATUS.JUDGMENT_RULE
  if (types.length === 1 && types[0] === 'remove') return EXPORT_STATUS.REMOVED
  return EXPORT_STATUS.CONFIGURED
}

/**
 * マッピング未設定 / 不要の行を生成する（F 列以降は空欄）
 *
 * @param {{ categoryName: string; dataKindName: string; columnName: string; dataType: string }} base
 * @param {string} status
 * @param {string} [content]
 * @returns {MappingExportRow}
 */
function createPlainRow(base, status, content = '') {
  return {
    ...base,
    status,
    mappingMethod: '',
    priority: '',
    numAggType: '',
    fileType: '',
    sourceColumnName: '',
    content
  }
}

/**
 * マッピング 1 件（優先度 1 件）を Excel 行へ変換する
 *
 * @param {{ categoryName: string; dataKindName: string; columnName: string; dataType: string }} base
 * @param {Record<string, unknown>} mappingRow
 * @param {string} status
 * @param {{
 *   conversionTableList?: { conversionTableKey?: string; conversionTableName?: string }[]
 *   judgmentRule?: Record<string, unknown> | null
 * }} options
 * @param {boolean} showPriority - 優先度・集計方法を出力するか（マッピングが 2 件以上のときのみ true）
 * @returns {MappingExportRow}
 */
function createMappingRow(base, mappingRow, status, options, showPriority) {
  const mappingType = pick(mappingRow, 'mapping_type', 'mappingType')
  const mappingInfo = pick(mappingRow, 'mapping_info', 'mappingInfo') ?? {}
  const { fileType, sourceColumnName } = readSourceColumn(mappingInfo)

  return {
    ...base,
    status,
    mappingMethod: formatMappingMethodLabel(mappingType),
    // 優先度・集計方法はマッピングが 2 件以上あるときだけ意味を持つため、
    // 1 件のみの項目では空欄にする（余計な情報でノイズにしない）
    priority: showPriority ? Number(pick(mappingRow, 'priority', 'Priority') ?? 0) || '' : '',
    numAggType: showPriority ? formatNumAggTypeLabel(pick(mappingRow, 'num_agg_type', 'numAggType')) : '',
    fileType,
    sourceColumnName,
    content: formatMappingContent(mappingType, mappingInfo, options)
  }
}

/**
 * Excel 出力用の行配列を構築する。
 *
 * @param {{
 *   genericDataKinds?: unknown[]
 *   allChrofyColumns?: unknown[]
 *   mappings?: unknown[]
 *   judgmentRules?: unknown[]
 *   conversionTableList?: { conversionTableKey?: string; conversionTableName?: string }[]
 *   currentQuarter?: string | null
 * }} params
 * @returns {MappingExportRow[]}
 */
export function buildMappingExportRows(params = {}) {
  const { genericDataKinds, allChrofyColumns, mappings, judgmentRules, conversionTableList, currentQuarter } = params

  const columnsByDataKind = buildColumnsByDataKind(allChrofyColumns ?? [], currentQuarter)
  const mappingsByField = buildMappingsByField(mappings ?? [])
  const judgmentRulesByField = buildJudgmentRulesByField(judgmentRules ?? [])

  /** @type {MappingExportRow[]} */
  const rows = []

  // IF02095 の順序（表示順）をそのまま踏襲する
  for (const kind of genericDataKinds ?? []) {
    const kindRecord = asRecord(kind)
    const dataKindCode = String(pick(kindRecord, 'genericDataKindCode', 'generic_data_kind_code') ?? '').trim()
    if (dataKindCode === '' || SYSTEM_ONLY_DATA_KIND_CODES.has(dataKindCode)) continue

    const columns = columnsByDataKind.get(dataKindCode)
    // IF02090 に項目定義が無いデータ集類は行を作れない
    if (!columns || columns.length === 0) continue

    const categoryName = String(pick(kindRecord, 'genericDataSuperKindName', 'generic_data_super_kind_name') ?? '')
    const dataKindName = String(pick(kindRecord, 'genericDataKindName', 'generic_data_kind_name') ?? dataKindCode)

    for (const column of columns) {
      const base = {
        categoryName,
        dataKindName,
        columnName: column.columnName,
        dataType: formatDataTypeLabel(column.dataType)
      }
      const fieldKey = `${dataKindCode}::${column.columnCode}`
      const mappingRows = mappingsByField.get(fieldKey) ?? []
      const status = resolveStatus(mappingRows)

      if (status === EXPORT_STATUS.UNSET || status === EXPORT_STATUS.REMOVED) {
        rows.push(createPlainRow(base, status))
        continue
      }

      if (status === EXPORT_STATUS.JUDGMENT_RULE) {
        const judgmentRule = judgmentRulesByField.get(fieldKey) ?? judgmentRulesByField.get(column.columnCode) ?? null
        rows.push({
          ...createPlainRow(base, status, formatJudgmentRuleContent(judgmentRule)),
          mappingMethod: formatMappingMethodLabel('judgment_rule')
        })
        continue
      }

      // 優先度・集計方法はマッピングが複数あるときだけ出力する
      const showPriority = mappingRows.length > 1
      for (const mappingRow of mappingRows) {
        rows.push(createMappingRow(base, mappingRow, status, { conversionTableList }, showPriority))
      }
    }
  }

  return rows
}
